import { cx } from '@emotion/css';
import type { FC, ReactNode } from 'react';

import type { BackgroundColor } from '../../constants';
import { getMotifSchemeForLegacyBackgroundColor, getMotifVariableValue, m } from '../../motif';
import { MotifComponent, MotifScheme } from '../../motif/motifConstants';
import { useMotifStyles } from '../../motif/motifReactUtils';
import {
  backgroundColorCss,
  bottomHalfOverlaidCss,
  bottomOverlaidCss,
  breakCss,
  patternCss,
  topHalfOverlaidCss,
  topOverlaidCss,
} from './Break.styled';
import {
  HeadBreak,
  HeadFlippedBreak,
  Skirt2Break,
  Skirt3Break,
  SkirtBreak,
  StraightBreak,
} from './templates';
import { BreakOverlayType, Template } from './types';
import { getBreakOverlayType } from './utils';

export interface BreakProps {
  template: Template;
  topColor?: BackgroundColor | MotifScheme;
  bottomColor?: BackgroundColor | MotifScheme;
  isOverlaid?: boolean;
}

/** Spacer between blocks. */
export const Break: FC<BreakProps> = ({ template, topColor, bottomColor, isOverlaid }) => {
  useMotifStyles(MotifComponent.BREAK);

  const isTopTransparent = !topColor;
  const isBottomTransparent = !bottomColor;

  if (isTopTransparent && isBottomTransparent) return null;

  const topScheme = getMotifSchemeForLegacyBackgroundColor(topColor);
  const bottomScheme = getMotifSchemeForLegacyBackgroundColor(bottomColor);
  const topValue = isTopTransparent
    ? 'transparent'
    : getMotifVariableValue(topScheme, '--bg-color') ?? 'transparent';
  const bottomValue = isBottomTransparent
    ? 'transparent'
    : getMotifVariableValue(bottomScheme, '--bg-color') ?? 'transparent';

  const shadowColor =
    bottomScheme === MotifScheme.SECONDARY
      ? m('--break-shadow-light-color')
      : m('--break-shadow-dark-color');

  let shadowOpacity = 0.2;

  // Increase opacity if both top and bottom colors are dark
  if (topScheme === MotifScheme.SECONDARY && bottomScheme === MotifScheme.SECONDARY) {
    shadowOpacity = 0.4;
  }

  // Increase opacity if top color is light and bottom color is dark
  if (topScheme !== MotifScheme.SECONDARY && bottomScheme === MotifScheme.SECONDARY) {
    shadowOpacity = 0.56;
  }

  let pattern: ReactNode;

  switch (template) {
    case Template.Head:
      pattern = (
        <HeadBreak
          className={patternCss}
          topColor={topValue}
          bottomColor={bottomValue}
          shadowColor={shadowColor}
          shadowOpacity={shadowOpacity}
        />
      );
      break;
    case Template.HeadFlipped:
      pattern = (
        <HeadFlippedBreak
          className={patternCss}
          topColor={topValue}
          bottomColor={bottomValue}
          shadowColor={shadowColor}
          shadowOpacity={shadowOpacity}
        />
      );
      break;
    case Template.Skirt:
      pattern = (
        <SkirtBreak
          className={patternCss}
          topColor={topValue}
          bottomColor={bottomValue}
          shadowColor={shadowColor}
          shadowOpacity={shadowOpacity}
        />
      );
      break;
    case Template.Skirt2:
      pattern = (
        <Skirt2Break
          className={patternCss}
          topColor={topValue}
          bottomColor={bottomValue}
          shadowColor={shadowColor}
          shadowOpacity={shadowOpacity}
        />
      );
      break;
    case Template.Skirt3:
      pattern = (
        <Skirt3Break
          className={patternCss}
          topColor={topValue}
          bottomColor={bottomValue}
          shadowColor={shadowColor}
          shadowOpacity={shadowOpacity}
        />
      );
      break;
    case Template.Straight:
      pattern = (
        <StraightBreak
          className={patternCss}
          topColor={topValue}
          bottomColor={bottomValue}
          shadowColor={shadowColor}
          shadowOpacity={shadowOpacity}
        />
      );
      break;
    default:
      break;
  }

  if (!pattern) return null;

  // Check to see if this 'Break' will overlay another block/component
  // and, therefore, require a negative margin
  const breakOverlayType = getBreakOverlayType({
    template,
    isTopTransparent,
    isBottomTransparent,
    isOverlaid,
  });

  return (
    <div
      className={cx(
        MotifComponent.BREAK,
        /* It doesn't matter which background class name is used here as all set global variables to the same global colours
         * This is just set as a backup in case there is no background class name set higher up in the DOM, in which case
         * the global variables would not be set.
         *
         * We will use the theme to match the top color, which will be used to set the matching background color
         * to prevent the page background from bleeding through between the top and bottom SVG paths of the break.
         * This is especially apparent when the top and bottom are the same color.
         */
        topScheme ?? MotifScheme.DEFAULT,
        breakCss,
        {
          [topOverlaidCss]: breakOverlayType === BreakOverlayType.TOP,
          [bottomOverlaidCss]: breakOverlayType === BreakOverlayType.BOTTOM,
          [topHalfOverlaidCss]:
            breakOverlayType === BreakOverlayType.TOP_HALF_AND_BOTTOM_HALF ||
            breakOverlayType === BreakOverlayType.TOP_HALF,
          [bottomHalfOverlaidCss]:
            breakOverlayType === BreakOverlayType.TOP_HALF_AND_BOTTOM_HALF ||
            breakOverlayType === BreakOverlayType.BOTTOM_HALF,
          [backgroundColorCss]: !isTopTransparent && !isBottomTransparent,
        }
      )}
      data-testid={`break-${template?.toLowerCase()}`}
    >
      {pattern}
    </div>
  );
};

Break.displayName = 'Break';
